import { GetStaticProps } from "next";
import { useRouter } from "next/router";
import { useState } from "react";

import { useForgotPasswordMutation } from "@saas/account/data-access/@mutation/forgot-password/use-forgot-password.mutation";
import { usePostMetadataRecoveryMutation } from "@saas/account/data/@mutation/post-metadata-recovery/use-post-metadata-recovery.mutation";
import { AccountActions } from "@saas/account/feature/account-actions/account-actions";
import { AccountMenu } from "@saas/account/feature/account-menu/account-menu";
import { env } from "@saas/config/shared/env/env";
import { AppHead } from "@saas/layout/ui/app-head/app-head";
import { BaseLayout } from "@saas/layout/ui/base-layout/base-layout";
import { Header } from "@saas/layout/ui/header/header";
import { BaseButton } from "@saas/shared/ui/base-button/base-button";
import { BaseInput } from "@saas/shared/ui/base-input/base-input";

import { NextPageWithLayout } from "../_app";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

const schema = z.object({
  email: z
    .string()
    .min(1, "Wajib diisi.")
    .email({ message: "Harap isi dengan format yang benar." }),
});

export type ForgotPasswordFormData = { email: string };

export const ForgotPassword: NextPageWithLayout = () => {
  const router = useRouter();
  const [passwordRecoverySent, setPasswordRecoverySent] = useState(false);

  const {
    mutateAsync: mutateMetadataRecovery,
    isLoading: isMetadataRecoveryLoading,
  } = usePostMetadataRecoveryMutation();

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    watch,
  } = useForm<ForgotPasswordFormData>({
    reValidateMode: "onBlur",
    mode: "onBlur",
    resolver: zodResolver(schema),
  });

  const {
    mutateAsync: recoverAccount,
    recoveryData,
    isLoading: isForgotPasswordLoading,
  } = useForgotPasswordMutation();

  const emailValue = watch("email");

  const handleRecovery = async (data: ForgotPasswordFormData) => {
    try {
      await recoverAccount(
        { email: data.email },
        {
          onSuccess: () => {
            setPasswordRecoverySent(true);
          },
        }
      );

      await mutateMetadataRecovery({
        email: data.email,
        recoveryFrom: env.DASHBOARD_URL,
      });

      setPasswordRecoverySent(true);
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const handleRedirectToLogin = () => {
    router.push("/login");
    reset({ email: "" });
  };

  return (
    <>
      <AppHead>Lupa Kata Sandi</AppHead>
      <div
        className={
          "bg-blue-B500 z-1 mx-auto -mt-[90px] flex min-h-screen w-full justify-center px-5 md:px-6 2xl:items-center"
        }
      >
        {!passwordRecoverySent ? (
          <form
            onSubmit={handleSubmit(handleRecovery)}
            className={
              "bg-neutral-N0 mt-[100px] flex h-max max-w-lg flex-col gap-6 rounded-2xl p-6 md:mt-[100px] md:p-24"
            }
          >
            <p
              className={"text-[22px] font-bold md:text-[32px]"}
              data-testid={"forgot-password__text__title"}
            >
              Lupa Kata Sandi
            </p>
            <p
              className={"text-base"}
              data-testid={"forgot-password__text__subtitle"}
            >
              Masukkan email kamu yang terdaftar di {env.APP_NAME}. Instruksi
              perubahan email akan dikirimkan ke email tersebut.
            </p>

            <BaseInput
              testid={"forgot-password__text-field__email"}
              placeholder={"Email"}
              label={"Email"}
              type={"email"}
              error={!!errors.email?.message?.length}
              hint={errors.email?.message || ""}
              {...register("email")}
            />

            <BaseButton
              testid={"forgot-password__button__send-instruction"}
              disabled={!recoveryData?.id}
              isLoading={isForgotPasswordLoading || isMetadataRecoveryLoading}
              variant={"primary"}
              size={"big"}
              type={"submit"}
            >
              Kirim Instruksi
            </BaseButton>
          </form>
        ) : (
          <div
            className={
              "bg-neutral-N0 mt-[100px] flex h-max max-w-lg flex-col gap-6 rounded-2xl p-6 md:mt-[100px] md:p-24"
            }
          >
            <p
              className={"text-[22px] font-bold md:text-[32px]"}
              data-testid={"forgot-password-popup__text__title"}
            >
              Instruksi Terkirim
            </p>

            <p
              className={"text-base"}
              data-testid={"forgot-password-popup__text__subtitle"}
            >
              {`Kami telah mengirimkan instruksi untuk mengganti kata sandi ke email ${emailValue}, cek kotak masuk dan juga spam-mu.`}
            </p>
            <BaseButton
              testid={"forgot-password-popup__button__ok"}
              disabled={!recoveryData?.id}
              variant={"primary"}
              size={"big"}
              onClick={handleRedirectToLogin}
            >
              OK
            </BaseButton>
          </div>
        )}
      </div>
    </>
  );
};

ForgotPassword.getLayout = (page, _, flags) => {
  return (
    <BaseLayout
      header={
        <Header
          rightNavBar={
            <AccountActions
              accountMenu={AccountMenu}
              showActionButtons={false}
            ></AccountActions>
          }
          sticky={true}
          transparent={true}
          whiteMenu={true}
        />
      }
    >
      {page}
    </BaseLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};

export default ForgotPassword;
